import { useState } from "react";
import { Clock, Flame, Users, ChefHat, CheckCircle2, Camera, ImageMinus } from "lucide-react";

/* ════════════════════════════════════════════════════════════════════════
   RECIPE PRINT CARD — reusable, printable, cookbook-style recipe page.

   Uses the existing Home OS font (font-home). Elegance comes from
   spacing, weight and letter-spacing — no external fonts.

   Layout:
     • Title top-left · Category badge top-right
     • Image left (under title) · Meta panel right
     • Two columns: Preparation (left) · Ingredients (right)
     • Notes bottom

   `editable` toggles in-place image controls only. Field editing lives
   in RecipeEditModal — this stays a clean read/print surface.
   ════════════════════════════════════════════════════════════════════════ */

export function RecipePrintCard({ recipe, editable = false, onChangeImage, onRemoveImage }) {
  const [checked, setChecked] = useState({});
  if (!recipe) return null;
  const total = (recipe.prepTime || 0) + (recipe.cookTime || 0);

  return (
    <article
      className="print-recipe-card font-home bg-white text-gray-900 mx-auto w-full max-w-3xl border border-gray-200 rounded-[20px] shadow-sm overflow-hidden print:border print:border-gray-300 print:shadow-none print:rounded-none print:max-w-none print:break-inside-avoid"
      data-testid={`recipe-print-card-${recipe.id || "new"}`}
    >
      {/* Header — title + category badge */}
      <header className="px-8 pt-7 pb-3 flex items-start justify-between gap-4 border-b border-gray-100">
        <div className="min-w-0">
          <p className="text-[10px] font-extrabold uppercase tracking-[0.3em] text-gray-400">Recipe</p>
          <h1
            className="text-4xl sm:text-5xl font-extrabold leading-[1.05] tracking-tight text-gray-900 mt-1"
            data-testid="recipe-card-title"
          >
            {recipe.title || "Untitled recipe"}
          </h1>
          {recipe.subtitle && (
            <p className="text-base text-gray-500 mt-2 italic max-w-md leading-snug">{recipe.subtitle}</p>
          )}
        </div>
        <span
          className="inline-flex items-center px-3 py-1 rounded-full bg-gray-900 text-white text-[10px] font-extrabold uppercase tracking-[0.18em] flex-shrink-0"
          data-testid="recipe-card-category"
        >
          {recipe.category || "Recipe"}
        </span>
      </header>

      {/* Image + meta */}
      <div className="grid grid-cols-1 sm:grid-cols-5 gap-5 px-8 pt-6">
        {/* Image */}
        <div className="sm:col-span-3" data-testid="recipe-card-image">
          <ImagePanel recipe={recipe} editable={editable} onChangeImage={onChangeImage} onRemoveImage={onRemoveImage} />
        </div>

        {/* Meta */}
        <aside className="sm:col-span-2 flex flex-col gap-2" data-testid="recipe-card-meta">
          <MetaRow icon={Clock}  label="Prep"       value={`${recipe.prepTime || 0} min`} />
          <MetaRow icon={Flame}  label="Cook"       value={`${recipe.cookTime || 0} min`} />
          <MetaRow icon={Users}  label="Serves"     value={recipe.serves || "—"} />
          <MetaRow icon={ChefHat} label="Difficulty" value={recipe.difficulty || "Easy"} />
          {(recipe.tags || []).length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {recipe.tags.map(t => (
                <span key={t} className="inline-flex items-center px-2 py-0.5 rounded-full border border-gray-200 text-gray-700 text-[10px] font-extrabold uppercase tracking-wider">
                  {t}
                </span>
              ))}
            </div>
          )}
          <p className="text-[10px] uppercase tracking-wider text-gray-400 mt-1">Total · {total} min</p>
        </aside>
      </div>

      {/* Two columns: Preparation (left) + Ingredients (right) */}
      <div className="px-8 pt-7 pb-2 grid grid-cols-1 md:grid-cols-5 gap-7">
        <section className="md:col-span-3" data-testid="recipe-card-preparation">
          <SectionTitle>Preparation</SectionTitle>
          {recipe.instructions?.length ? (
            <ol className="space-y-3">
              {recipe.instructions.map((step, idx) => (
                <li key={idx} className="flex gap-3">
                  <span className="text-base font-extrabold text-gray-900 tabular-nums flex-shrink-0 mt-0.5">{String(idx + 1).padStart(2, "0")}</span>
                  <p className="text-[15px] leading-[1.55] text-gray-800">{step}</p>
                </li>
              ))}
            </ol>
          ) : (
            <p className="text-sm text-gray-400 italic">No preparation steps yet.</p>
          )}
        </section>

        <section className="md:col-span-2" data-testid="recipe-card-ingredients">
          <SectionTitle>Ingredients</SectionTitle>
          {recipe.ingredients?.length ? (
            <ul className="space-y-1.5">
              {recipe.ingredients.map((ing, idx) => {
                const ck = !!checked[idx];
                return (
                  <li key={idx} className="flex items-start gap-2">
                    <button
                      type="button"
                      onClick={() => setChecked(c => ({ ...c, [idx]: !c[idx] }))}
                      className="mt-1 flex-shrink-0 print:hidden"
                      aria-label="Toggle ingredient"
                    >
                      {ck
                        ? <CheckCircle2 className="w-4 h-4 text-[color:var(--ara-ink-muted)]" strokeWidth={1.5} />
                        : <span className="w-3.5 h-3.5 rounded-full border border-gray-300 inline-block" />}
                    </button>
                    <span className="hidden print:inline-block w-2 h-2 mt-2 rounded-full bg-gray-800 flex-shrink-0" />
                    <span className={`text-[14px] leading-snug text-gray-800 ${ck ? "line-through text-gray-400" : ""}`}>{ing}</span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-sm text-gray-400 italic">No ingredients yet.</p>
          )}
        </section>
      </div>

      {/* Notes */}
      {recipe.notes && (
        <footer className="px-8 pt-2 pb-7" data-testid="recipe-card-notes">
          <div className="border-t border-gray-100 pt-4">
            <p className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-gray-400 mb-1">Notes</p>
            <p className="text-[14px] leading-snug text-gray-700 italic">{recipe.notes}</p>
          </div>
        </footer>
      )}

      {recipe.source && !recipe.notes && (
        <p className="px-8 pb-5 text-[10px] uppercase tracking-wider text-gray-400">{recipe.source}</p>
      )}
    </article>
  );
}

/* ── inner pieces ─────────────────────────────────────────────────── */

function SectionTitle({ children }) {
  return (
    <h2 className="text-[11px] font-extrabold uppercase tracking-[0.28em] text-gray-500 mb-3 pb-2 border-b border-gray-200">
      {children}
    </h2>
  );
}

function MetaRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2.5 border-b border-gray-100 pb-2">
      <Icon className="w-4 h-4 text-gray-500 flex-shrink-0" strokeWidth={1.5} />
      <div className="flex-1 min-w-0 flex items-baseline justify-between gap-2">
        <p className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-gray-400">{label}</p>
        <p className="text-sm font-extrabold text-gray-900 truncate">{value}</p>
      </div>
    </div>
  );
}

function ImagePanel({ recipe, editable, onChangeImage, onRemoveImage }) {
  const src = recipe.imageUrl;
  return (
    <div className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden     border border-gray-200 print:border-gray-300">
      {src ? (
        <img src={src} alt={recipe.title || "Recipe"} className="absolute inset-0 w-full h-full object-cover" data-testid="recipe-card-image-img" />
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-7xl sm:text-8xl" aria-hidden>{recipe.imageEmoji || "🍽️"}</span>
          <p className="mt-2 text-[10px] uppercase tracking-wider text-gray-400">{recipe.title || "Recipe image"}</p>
        </div>
      )}

      {editable && (
        <div className="absolute bottom-2 right-2 flex gap-1.5 print:hidden">
          <button
            type="button"
            onClick={onChangeImage}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/90 backdrop-blur border border-gray-200 text-gray-700 text-[10px] font-extrabold shadow-sm hover:bg-white"
            data-testid="recipe-card-change-image"
          >
            <Camera className="w-3 h-3" strokeWidth={3} /> Change image
          </button>
          {src && (
            <button
              type="button"
              onClick={onRemoveImage}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/90 backdrop-blur border border-gray-200 text-[color:var(--ara-ink-muted)] text-[10px] font-extrabold shadow-sm hover:bg-white"
              data-testid="recipe-card-remove-image"
            >
              <ImageMinus className="w-3 h-3" strokeWidth={3} /> Remove
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Print CSS — scoped to recipe surfaces. Import this once. ─────── */

export function RecipePrintStyles() {
  return (
    <style>{`
      @media print {
        @page { margin: 16mm; size: A4; }
        body { background: white !important; }
        .no-print, .no-print * { display: none !important; }
        /* Hide app navigation + sub-nav + buttons inside print area */
        nav, [data-testid="home-os-subnav"], aside.app-sidebar { display: none !important; }
        .print-recipe-card { box-shadow: none !important; border-color: #e5e7eb !important; }
        .print-recipe-card h1 { color: #111827 !important; }
      }
    `}</style>
  );
}

/* ── Image picker helper — downsizes to base64 for safe localStorage ── */

export function pickRecipeImage(onResult) {
  if (typeof document === "undefined") return;
  const input = document.createElement("input");
  input.type = "file";
  input.accept = "image/*";
  input.onchange = async () => {
    const file = input.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await readAsDataURL(file);
      const resized = await downsizeImage(dataUrl, 900, 700, 0.82);
      onResult?.(resized);
    } catch {
      onResult?.(null);
    }
  };
  input.click();
}

function readAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function downsizeImage(dataUrl, maxW, maxH, quality) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const ratio = Math.min(maxW / img.width, maxH / img.height, 1);
      const w = Math.round(img.width * ratio);
      const h = Math.round(img.height * ratio);
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}
